import React, { useMemo } from 'react';
import { get } from 'lodash';
import { string, bool, number, shape, arrayOf } from 'prop-types';
import { FormattedMessage, FormattedHTMLMessage, injectIntl, intlShape } from 'react-intl';
import classNames from 'classnames';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { PackageCard } from '@landtrustinc/design-system';
import { formatMoney } from '../../util/currency';
import { formatNumber } from '../../util/data';
import { types as sdkTypes } from '../../util/sdkLoader';
import config from '../../config';
import { defaultCommission } from '../../marketplace-custom-config';
import { getStartingPrice, LODGING_INFO, getRVDetails } from '../../util/package';
import { getLodgingPricesOptions } from '../../util/listing';
import usePackageBadges from '../PackageBadges/hooks/usePackageBadges';

import css from './PackageCardPreview.module.css';

const { Money } = sdkTypes;

const PackageCardPreview = ({
  intl,
  item,
  images,
  className,
  currentListing,
  currentUser,
  hasFlexiblePricing,
}) => {
  const classes = classNames(css.root, className);
  const { guests, title, days, price, lodgingPrices: lodgingPricesValues, flexiblePricing } = item;

  const priceAmount = price?.amount || 0;

  const lodgingPricesOptions = getLodgingPricesOptions(currentListing, intl);

  const lodgingPrices = lodgingPricesOptions.map((option, index) => {
    return {
      ...option,
      option: lodgingPricesValues?.[index],
    };
  });

  const optionalLodging = (lodgingPrices || []).filter(
    lodgingPrice => lodgingPrice.option === LODGING_INFO.OPTIONAL
  );
  const includedLodging = (lodgingPrices || []).filter(
    lodgingPrice => lodgingPrice.option === LODGING_INFO.REQUIRED
  );

  const { isRvFriendly, hasRVSites } = getRVDetails(currentListing, { lodgingPrices });

  const packageItem = {
    price: price && JSON.stringify(price),
    guests,
    days,
    optionalLodging,
    includedLodging,
    flexiblePricing,
  };

  const startingPrice = getStartingPrice(packageItem);

  // Use the badges hook with override info for computed values
  const badges = usePackageBadges(
    item,
    {
      hasLodging: optionalLodging.length > 0 || includedLodging.length > 0,
      isRvFriendly,
      hasRVSites,
      hasFlexiblePricing,
    },
    currentListing
  );

  // Build subtitle from listing data (same as usePackageData)
  const subtitle = useMemo(() => {
    const publicData = currentListing?.attributes?.publicData || {};
    const listingTitle = currentListing?.attributes?.title || '';
    const nearestTown = get(publicData, 'nearestTown.selectedPlace.address', null);
    const acreage = get(publicData, 'location.acreage', 0);
    const formattedAcres = acreage ? formatNumber(acreage) : null;

    if (!formattedAcres && !listingTitle) return null;

    return intl.formatMessage(
      {
        id: 'PackageCard.subtitle',
        defaultMessage: '{acres} Acres at {listingTitle}{locationText}',
      },
      {
        acres: formattedAcres,
        listingTitle,
        locationText: nearestTown ? ` in ${nearestTown}` : '',
      }
    );
  }, [currentListing, intl]);

  // Calculate potential earnings
  const userCommission =
    get(currentUser, 'attributes.profile.privateData.commission.commissionRate') ??
    get(currentListing, 'attributes.profile.privateData.commission.commissionRate') ??
    defaultCommission.commissionRate;

  // Use effective min guests for calculations (1 if min is 0)
  const effectiveMinGuests = guests?.min === 0 ? 1 : guests?.min;

  const commission = startingPrice?.amount * effectiveMinGuests * (userCommission / 100);

  const minEarningAmount = new Money(
    startingPrice?.amount * effectiveMinGuests + commission,
    config.currency
  );

  const potentialEarnings = formatMoney(intl, minEarningAmount);
  const showPotentialEarnings = priceAmount > 0 && guests?.max >= 0 && effectiveMinGuests > 0;

  // Check if we have enough data to show the card
  const hasMinimumData = title || price?.amount;

  return (
    <div className={classes}>
      {hasMinimumData ? (
        <PackageCard
          badges={badges}
          images={images}
          orientation="vertical"
          subtitle={subtitle}
          title={title || intl.formatMessage({ id: 'PackageCardPreview.untitled' })}
        />
      ) : (
        <div className={css.emptyState}>
          <div className={css.emptyStateTitle} />
          <div className={css.emptyStatePrice} />
          <div className={css.emptyStateBadges} />
        </div>
      )}

      <div className={css.earningsSection}>
        {showPotentialEarnings ? (
          <div className={css.earnings}>
            <FormattedMessage
              id="PackageCard.estimatedEarnings"
              values={{
                earnings: potentialEarnings,
              }}
            />
            <div className={css.earningsFooter}>
              <FormattedHTMLMessage id="PackageCard.previewFooter" />
            </div>
          </div>
        ) : (
          <div className={css.earningsEmptyState} />
        )}
      </div>
    </div>
  );
};

PackageCardPreview.defaultProps = {
  images: [],
  className: null,
  currentUser: null,
  hasFlexiblePricing: false,
};

PackageCardPreview.propTypes = {
  intl: intlShape.isRequired,
  item: shape({
    title: string,
    description: string,
    days: shape({
      min: number,
      max: number,
    }),
    guests: shape({
      min: number,
      max: number,
    }),
  }).isRequired,
  images: arrayOf(string),
  className: string,
  // eslint-disable-next-line react/forbid-prop-types
  currentListing: shape({}).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  currentUser: shape({}),
  hasFlexiblePricing: bool,
};

const mapStateToProps = state => ({
  currentUser: state.user.currentUser,
});

export default compose(
  connect(mapStateToProps),
  injectIntl
)(PackageCardPreview);
